import * as React from 'react';
import { useDispatch } from 'react-redux';
import { IStringLocalizer } from '../../../utilities/IStringLocalizer';
import { useLocalizer } from './hooks/useLocalizer';
import {
    actionCreators as aiActionCreators
} from '../../../store/StateControllers/Survey/SurveyResponse/AISurveyAgentController';

interface AIUploadDifferentFilesButtonProps {
    onError?: (message: string) => void;
    localizer?: IStringLocalizer;
    disabled?: boolean;
}

/**
 * AIUploadDifferentFilesButton Component
 * 
 * Self-contained button that handles:
 * - Upload different files button UI
 * - Clearing AI operation state (resets to ready-to-process step)
 * - Files remain selected and visible in the file list
 * - Error handling
 * 
 * Note: This does NOT clear uploaded files - it only clears the AI processing state
 * so the user returns to the file selection view with their files still selected.
 */
export const AIUploadDifferentFilesButton: React.FC<AIUploadDifferentFilesButtonProps> = ({
    onError,
    localizer,
    disabled = false
}) => {
    const dispatch = useDispatch();
    const localize = useLocalizer(localizer);
    const buttonRef = React.useRef<HTMLButtonElement | null>(null);

    React.useEffect(() => {
        if (!buttonRef.current || typeof $ === "undefined" || typeof $.fn?.popover !== "function") {
            return;
        }

        const popover = $(buttonRef.current);
        popover.popover("destroy");

        if (disabled) {
            return;
        }

        popover.popover({
            trigger: "hover focus",
            container: "body"
        });

        return () => {
            popover.popover("destroy");
        };
    }, [disabled]);

    /**
     * Handles uploading different files by:
     * 1. Clearing AI operation state
     * 2. Keeping files selected in the file list
     * 3. Returning UI to the "ready to process" state
     */
    const handleUploadDifferentFiles = () => {
        if (disabled) {
            return;
        }
        try {
            // Clear AI operation - resets processing state but preserves uploaded files
            dispatch(aiActionCreators.clearAIOperation());
        } catch (error) {
            if (onError) {
                const errorMsg = localize("Failed to clear AI operation. Please try again.");
                onError(errorMsg);
            }
        }
    };

    return (
        <button
            ref={buttonRef}
            type="button"
            onClick={handleUploadDifferentFiles}
            disabled={disabled}
            aria-disabled={disabled}
            className={`btn ai-upload-different-button${disabled ? " disabled" : ""}`}
            data-toggle={disabled ? undefined : "popover"}
            data-container={disabled ? undefined : "body"}
            data-placement={disabled ? undefined : "bottom"}
            data-content={disabled ? undefined : localize("Upload all relevant files again")}
        >
            {localize("Upload Different Files")}
        </button>
    );
};

export default AIUploadDifferentFilesButton;
